import _ from "lodash"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { findTokenAtCursor, replaceTokenAtCursor } from "authoring/routes/macro/lib/macro-token-utils"

const { debounce, uniqBy } = _
const DEFAULT_DEBOUNCE_MS = 500

export function useMacroAutocomplete({
    macros = [],
    contextMacros = [],
    debounceMs = DEFAULT_DEBOUNCE_MS,
    onInsert,
    getValue,
    setValue,
    getCursorIndex,
    setCursorIndex,
} = {}) {
    const [open, setOpen] = useState(false)
    const [query, setQuery] = useState("")
    const [activeIndex, setActiveIndex] = useState(0)
    const contextRef = useRef(null)

    const mergedMacros = useMemo(() => uniqBy([...contextMacros, ...macros], "id"), [contextMacros, macros])
    const results = useMemo(() => filterMacros(mergedMacros, query), [mergedMacros, query])

    const openDebounced = useMemo(
        () =>
            debounce((context) => {
                contextRef.current = context
                setOpen(true)
                setQuery(context?.query ?? "")
                setActiveIndex(0)
            }, debounceMs),
        [debounceMs]
    )

    useEffect(() => () => openDebounced.cancel(), [openDebounced])

    useEffect(() => {
        if (!results.length && activeIndex !== 0) {
            setActiveIndex(0)
        } else if (activeIndex >= results.length && results.length) {
            setActiveIndex(0)
        }
    }, [activeIndex, results])

    const close = useCallback(() => {
        openDebounced.cancel()
        setOpen(false)
        setQuery("")
        setActiveIndex(0)
        contextRef.current = null
    }, [openDebounced])

    const handleInput = useCallback(
        ({ value, cursorIndex } = {}) => {
            const nextValue = resolveValue(value, getValue)
            const nextCursor = resolveCursorIndex(cursorIndex, getCursorIndex, nextValue)
            const context = resolveSearchContext(nextValue, nextCursor)
            contextRef.current = context

            if (!context) {
                openDebounced.cancel()
                if (open) {
                    close()
                }
                return
            }

            if (open) {
                setQuery(context.query)
                setActiveIndex(0)
                return
            }

            openDebounced(context)
        },
        [close, getCursorIndex, getValue, open, openDebounced]
    )

    const selectMacro = useCallback(
        (macro, options = {}) => {
            if (!macro?.id) return
            const value = resolveValue(options.value, getValue)
            if (value === null || value === undefined) return

            const cursorIndex = resolveCursorIndex(options.cursorIndex, getCursorIndex, value)
            const token = buildMacroToken(macro)
            if (!token) return
            const context = contextRef.current ?? resolveSearchContext(value, cursorIndex)

            if (typeof onInsert === "function") {
                const handled = onInsert({ macro, token, context, cursorIndex })
                if (handled) {
                    setOpen(false)
                    setQuery("")
                    setActiveIndex(0)
                    contextRef.current = null
                    return
                }
            }

            const { nextValue, nextCursor } = replaceWithToken(value, cursorIndex, token, context)

            if (setValue) {
                setValue(nextValue)
            }
            if (setCursorIndex && Number.isFinite(nextCursor)) {
                setCursorIndex(nextCursor)
            }

            setOpen(false)
            setQuery("")
            setActiveIndex(0)
            contextRef.current = null
        },
        [getCursorIndex, getValue, onInsert, setCursorIndex, setValue]
    )

    const handleKeyDown = useCallback(
        (event) => {
            if (!open) return false
            if (!event?.key) return false

            if (event.key === "ArrowDown") {
                event.preventDefault()
                setActiveIndex((index) => clampIndex(index + 1, results.length))
                return true
            }

            if (event.key === "ArrowUp") {
                event.preventDefault()
                setActiveIndex((index) => clampIndex(index - 1, results.length))
                return true
            }

            if (event.key === "Enter") {
                event.preventDefault()
                selectMacro(results[activeIndex])
                return true
            }

            if (event.key === "Escape") {
                event.preventDefault()
                close()
                return true
            }

            return false
        },
        [activeIndex, close, open, results, selectMacro]
    )

    return {
        open,
        query,
        setQuery,
        results,
        activeIndex,
        setActiveIndex,
        handleInput,
        handleKeyDown,
        selectMacro,
        close,
    }
}

function buildMacroToken(macro) {
    const id = `${macro?.id ?? ""}`.trim()
    if (!id) return ""
    return `$${id}$`
}

function resolveValue(value, getValue) {
    if (value !== undefined) return value
    if (!getValue) return ""
    return getValue()
}

function resolveCursorIndex(cursorIndex, getCursorIndex, value) {
    if (Number.isFinite(cursorIndex)) return cursorIndex
    if (getCursorIndex) {
        const resolved = getCursorIndex()
        if (Number.isFinite(resolved)) return resolved
    }
    return value?.length ?? 0
}

function resolveSearchContext(value, cursorIndex) {
    const text = toSafeString(value)
    if (!text) return null

    const index = clampIndex(cursorIndex ?? text.length, text.length + 1)
    if (index <= 0 || index > text.length) return null

    let start = index - 1
    while (start >= 0 && isTokenChar(text[start])) start--
    if (text[start] !== "$") return null
    if (text[start - 1] === "$") return null
    if (isClosingDelimiter(text, start)) return null
    if (index !== start + 1 && !isTokenChar(text[start + 1])) return null

    const query = text.slice(start + 1, index)
    return { start, end: index, query }
}

function replaceWithToken(value, cursorIndex, token, context) {
    if (context?.start !== undefined && context?.end !== undefined) {
        return {
            nextValue: replaceRange(value, context.start, context.end, token),
            nextCursor: context.start + token.length,
        }
    }

    const match = findTokenAtCursor(value, cursorIndex)
    if (match) {
        return {
            nextValue: replaceRange(value, match.start, match.end, token),
            nextCursor: match.start + token.length,
        }
    }

    const nextValue = replaceTokenAtCursor(value, cursorIndex, token)
    return {
        nextValue,
        nextCursor: (cursorIndex ?? value.length) + token.length,
    }
}

function replaceRange(value, start, end, nextValue) {
    const text = toSafeString(value)
    const safeStart = clampIndex(start, text.length + 1)
    const safeEnd = clampIndex(end, text.length + 1)
    return `${text.slice(0, safeStart)}${nextValue}${text.slice(safeEnd)}`
}

function filterMacros(macros = [], search = "") {
    if (!search) return macros
    const query = search.toLowerCase()
    return macros.filter((macro) => {
        const name = macro?.name?.toLowerCase() ?? ""
        const id = macro?.id?.toLowerCase() ?? ""
        return name.includes(query) || id.includes(query)
    })
}

function isTokenChar(char) {
    return char === "-" || char === "_" || char === ":" || char === "." || isAlphaNumeric(char)
}

function isAlphaNumeric(char) {
    if (!char) return false
    const code = char.charCodeAt(0)
    return (code >= 48 && code <= 57) || (code >= 65 && code <= 90) || (code >= 97 && code <= 122)
}

function isClosingDelimiter(text, index) {
    if (!text || text[index] !== "$") return false
    let pos = index - 1
    if (pos < 0 || !isTokenChar(text[pos])) return false
    while (pos >= 0 && isTokenChar(text[pos])) pos--
    return text[pos] === "$"
}

function toSafeString(value) {
    if (value === null || value === undefined) return ""
    return `${value}`
}

function clampIndex(value, length) {
    if (!Number.isFinite(value)) return 0
    if (value < 0) return 0
    if (value > length) return length
    return value
}
